import React, { useState, useEffect } from 'react';
import { RiBox3Line } from 'react-icons/ri';
import { MdInventory } from 'react-icons/md';
import { IoTrendingDown, IoAnalyticsOutline } from 'react-icons/io5';
import { IoIosReturnLeft } from 'react-icons/io';
import { FiDownload } from 'react-icons/fi';
import { toast } from 'react-toastify';
import Table from '../components/common/Table';
import { reportService, companyService } from '../services/api';
import {
 TotalProductsIcon,
 TotalBinsIcon,
 TotalProductsIcon as TotalUOMIcon, // agar alag UOM icon nahi hai to ye use kar lo
} from "../assets/states/StatsIcons";

const SummaryCard = ({
  label,
  value,
  Icon,
  bg,
  stroke = true,
  icon,
  iconBgColor = 'bg-gray-100',
  iconTextColor = 'text-gray-600',
}) => {
  const hasIconComponent = Icon && typeof Icon === 'function';
  const hasIconElement = icon != null && React.isValidElement(icon);

  const boxStyle = {
    width: 40,
    height: 40,
    borderTopLeftRadius: 20,
    borderTopRightRadius: 6,
    borderBottomRightRadius: 20,
    borderBottomLeftRadius: 6,
    ...(hasIconComponent && bg ? { background: bg } : {}),
  };

  return (
    <div className="rounded-2xl border border-gray-100 bg-white p-5 shadow-sm w-full min-w-0">
      <div className="flex items-center gap-5">
        <div
          className={`flex items-center justify-center flex-shrink-0 ${hasIconElement ? iconBgColor : ''}`}
          style={boxStyle}
        >
          {hasIconComponent && (stroke ? <Icon size={18} color="#FFFFFF" /> : <Icon size={24} />)}
          {hasIconElement && <span className={iconTextColor}>{icon}</span>}
        </div>
        <div className="min-w-0">
          <p className="text-xl font-semibold text-gray-900 leading-none">{value}</p>
          <p className="mt-2 text-sm font-medium text-gray-400 whitespace-nowrap truncate">
            {label}
          </p>
        </div>
      </div>
    </div>
  );
};


const REPORT_TABS = [
  { id: 'product', label: 'Inventory by Product' },
  { id: 'batch', label: 'Inventory by Batch' },
  { id: 'uom', label: 'Inventory by UOM' },
  { id: 'summary', label: 'Inventory by Summary' },
  { id: 'aging', label: 'Ageing Report' },
];

const PRODUCT_COLUMNS = [
  { key: 'productCode', label: 'Product Code' },
  { key: 'productDescription', label: 'Description' },
  { 
    key: 'companyName', 
    label: 'Company',
    render: (val) => val || '—'
  },
  { key: 'uom', label: 'UOM' },
  { key: 'openingStock', label: 'Opening' },
  { key: 'inwardQuantity', label: 'Inward' },
  { key: 'outwardQuantity', label: 'Outward' },
  { key: 'returnQuantity', label: 'Return' },
  { key: 'closingStock', label: 'Closing' },
];

const Pill = ({ text, variant = "blue" }) => {
 const map = {
   green: "border-emerald-500 text-emerald-600 bg-emerald-50/50",
   orange: "border-orange-300 text-orange-400 bg-orange-50/50",
   blue: "border-indigo-800 text-indigo-900 bg-indigo-50/40",
   gray: "border-gray-300 text-gray-700 bg-gray-50",
 };

 return (
   <span
     className={`inline-flex items-center justify-center rounded-full border px-4 py-1 text-sm font-medium whitespace-nowrap ${map[variant] || map.gray}`}
   >
     {text}
   </span>
 );
};

const BATCH_COLUMNS = [
 { key: 'productCode', label: 'Product Code' },
 { 
   key: 'companyName', 
   label: 'Company',
   render: (val) => val || '—'
 },
 { key: 'batchNumber', label: 'Batch Number' },
 {
   key: 'expiryDate',
   label: 'Exp Date',
   render: (val) => (val ? new Date(val).toLocaleDateString() : '—'),
 },
 { key: 'quantity', label: 'Quantity', align: 'right' },

 // ✅ condition pill (Expired / Good)
 {
   key: 'condition',
   label: 'Condition',
   render: (val) => {
     const v = String(val || '').toLowerCase();
     const isExpired = v.includes('expired');
     const isGood = v.includes('good');
     return (
       <Pill
         text={val || '—'}
         variant={isExpired ? 'orange' : isGood ? 'green' : 'gray'}
       />
     );
   },
 },

 { key: 'binCode', label: 'Bin Code' },

 // ✅ binType pill (blue)
 {
   key: 'binType',
   label: 'Bin Type',
   render: (val) => <Pill text={val || '—'} variant="blue" />,
 },
];


const UOM_COLUMNS = [
  { key: 'uom', label: 'UOM' },
  { key: 'productCode', label: 'Product Code' },
  { key: 'productDescription', label: 'Description' },
  { 
    key: 'companyName', 
    label: 'Company',
    render: (val) => val || '—'
  },
  { key: 'qnt', label: 'Qnt', align: 'right' },
  { key: 'holdQty', label: 'Hold Qnt', align: 'right' },
];

const EXPIRED_COLUMNS = [
  { key: 'productCode', label: 'Product Code' },
  { 
    key: 'companyName', 
    label: 'Company',
    render: (val) => val || '—'
  },
  { key: 'batchNumber', label: 'Batch Number' },
  {
    key: 'expiryDate',
    label: 'Exp Date',
    render: (val) => (val ? new Date(val).toLocaleDateString() : '—'),
  },
  // { 
  //   key: 'shelfLifeRemainingPercent', 
  //   label: '% Remaining',
  //   align: 'right',
  //   render: (val) => val != null ? `${val}%` : '—'
  // },
  { key: 'quantity', label: 'Quantity', align: 'right' },
  { key: 'binCode', label: 'Bin Code' },
];

const NEAR_EXPIRY_COLUMNS = [
  { key: 'productCode', label: 'Product Code' },
  { 
    key: 'companyName', 
    label: 'Company',
    render: (val) => val || '—'
  },
  { key: 'batchNumber', label: 'Batch Number' },
  {
    key: 'expiryDate',
    label: 'Exp Date',
    render: (val) => (val ? new Date(val).toLocaleDateString() : '—'),
  },
  { 
    key: 'shelfLifeRemainingPercent', 
    label: '% Remaining',
    align: 'right',
    render: (val) => val != null ? `${val}%` : '—'
  },
  { key: 'daysUntilExpiry', label: 'Days Left', align: 'right' },
  { key: 'quantity', label: 'Quantity', align: 'right' },
];

const CARD_TITLES = {
  product: 'Inventory Report (Product)',
  batch: 'Inventory Report (Batch)',
  uom: 'Inventory Report (UOM)',
  summary: 'Inventory Summary',
  aging: 'Ageing Report',
};

export function Reports() {
  const [activeReport, setActiveReport] = useState('product');
  const [productData, setProductData] = useState([]);
  const [batchData, setBatchData] = useState([]);
  const [uomData, setUomData] = useState([]);
  const [summaryData, setSummaryData] = useState(null);
  const [expiredBatches, setExpiredBatches] = useState([]);
  const [nearExpiryBatches, setNearExpiryBatches] = useState([]);
  const [ageingSummary, setAgeingSummary] = useState(null);
  const [loading, setLoading] = useState(false);
  const [downloading, setDownloading] = useState(false);
  const [selectedCompanyId, setSelectedCompanyId] = useState('');
  const [companies, setCompanies] = useState([]);

  const handleDownloadReport = async (reportType) => {
    try {
      setDownloading(true);
      let blob;
      let filename;
      const companyId = selectedCompanyId || undefined;

      switch (reportType) {
        case 'product':
          blob = await reportService.downloadInventoryReportByProduct(companyId);
          filename = `inventory_report_by_product_${new Date().toISOString().split('T')[0]}.xlsx`;
          break;
        case 'batch':
          blob = await reportService.downloadInventoryReportByBatch(companyId);
          filename = `inventory_report_by_batch_${new Date().toISOString().split('T')[0]}.xlsx`;
          break;
        case 'uom':
          blob = await reportService.downloadInventoryReportByUOM(companyId);
          filename = `inventory_report_by_uom_${new Date().toISOString().split('T')[0]}.xlsx`;
          break;
        case 'summary':
          blob = await reportService.downloadInventorySummary(companyId);
          filename = `inventory_summary_${new Date().toISOString().split('T')[0]}.xlsx`;
          break;
        case 'aging':
          blob = await reportService.downloadAgingReport(companyId);
          filename = `aging_report_${new Date().toISOString().split('T')[0]}.xlsx`;
          break;
        default:
          throw new Error('Invalid report type');
      }

      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = filename;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);
      toast.success('Report downloaded successfully');
    } catch (error) {
      console.error('Error downloading report:', error);
      toast.error(error?.message || 'Failed to download report');
    } finally {
      setDownloading(false);
    }
  };

  const fetchCompanies = async () => {
    try {
      const result = await companyService.getCompanies({ isActive: 'true' });
      setCompanies(result.data || []);
    } catch (error) {
      console.error('Error fetching companies:', error);
    }
  };

  const fetchInventoryByProduct = async () => {
    try {
      setLoading(true);
      const companyId = selectedCompanyId || undefined;
      const result = await reportService.getInventoryReportByProduct(companyId);
      if (result && result.success && Array.isArray(result.data)) {
        setProductData(result.data);
      } else {
        setProductData([]);
      }
    } catch (error) {
      console.error('Error fetching inventory by product:', error);
      setProductData([]);
    } finally {
      setLoading(false);
    }
  };

  const fetchInventoryByBatch = async () => {
    try {
      setLoading(true);
      const companyId = selectedCompanyId || undefined;
      const result = await reportService.getInventoryReportByBatch(companyId);
      if (result && result.success && Array.isArray(result.data)) {
        setBatchData(result.data);
      } else {
        setBatchData([]);
      }
    } catch (error) {
      console.error('Error fetching inventory by batch:', error);
      setBatchData([]);
    } finally {
      setLoading(false);
    }
  };

  const fetchInventoryByUOM = async () => {
    try {
      setLoading(true);
      const companyId = selectedCompanyId || undefined;
      const result = await reportService.getInventoryReportByUOM(companyId);
      if (result && result.success && Array.isArray(result.data)) {
        const flattened = [];
        for (const group of result.data) {
          for (const p of group.products || []) {
            flattened.push({
              uom: group.uom,
              productCode: p.productCode,
              productDescription: p.productDescription,
              companyName: p.companyName || '—',
              qnt: p.qnt || 0,
              holdQty: p.holdQty || 0,
            });
          }
        }
        setUomData(flattened);
      } else {
        setUomData([]);
      }
    } catch (error) {
      console.error('Error fetching inventory by UOM:', error);
      setUomData([]);
    } finally {
      setLoading(false);
    }
  };

  const fetchInventorySummary = async () => {
    try {
      setLoading(true);
      const companyId = selectedCompanyId || undefined;
      const result = await reportService.getInventorySummary(companyId);
      if (result && result.success && result.summary) {
        setSummaryData(result.summary);
      } else {
        setSummaryData(null);
      }
    } catch (error) {
      console.error('Error fetching inventory summary:', error);
      setSummaryData(null);
    } finally {
      setLoading(false);
    }
  };

  const fetchAgingReport = async () => {
    try {
      setLoading(true);
      const companyId = selectedCompanyId || undefined;
      const result = await reportService.getAgingReport(companyId);
      if (result && result.success) {
        setExpiredBatches(Array.isArray(result.expiredBatches) ? result.expiredBatches : []);
        setNearExpiryBatches(Array.isArray(result.nearExpiryBatches) ? result.nearExpiryBatches : []);
        setAgeingSummary(result.summary || null);
      } else {
        setExpiredBatches([]);
        setNearExpiryBatches([]);
        setAgeingSummary(null);
      }
    } catch (error) {
      console.error('Error fetching ageing report:', error);
      setExpiredBatches([]);
      setNearExpiryBatches([]);
      setAgeingSummary(null);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchCompanies();
  }, []);

  useEffect(() => {
    if (activeReport === 'product') fetchInventoryByProduct();
    else if (activeReport === 'batch') fetchInventoryByBatch();
    else if (activeReport === 'uom') fetchInventoryByUOM();
    else if (activeReport === 'summary') fetchInventorySummary();
    else if (activeReport === 'aging') fetchAgingReport();
  }, [activeReport, selectedCompanyId]);

  return (
    <div className="w-full pl-3 pr-6 py-6 bg-gray-50" style={{ minWidth: '800px' }}>
      <div
        className="flex flex-wrap items-center justify-between gap-4 w-full"
        style={{
          backgroundColor: '#FFFFFF',
          border: '1px solid #EEF1F5',
          borderRadius: 18,
          padding: '18px 22px',
        }}
      >
        <h1 style={{ fontSize: 22, fontWeight: 600, color: '#101828', margin: 0 }}>
          Reports
        </h1>
        <div
          className="overflow-x-auto"
          style={{
            border: '1px solid #D0D5DD',
            borderRadius: 14,
            height: 52,
            display: 'flex',
            alignItems: 'center',
            padding: 6,
            gap: 6,
          }}
        >
          {REPORT_TABS.map((tab) => (
            <button
              key={tab.id}
              type="button"
              onClick={() => setActiveReport(tab.id)}
              className="flex-shrink-0 whitespace-nowrap transition"
              style={{
                background: activeReport === tab.id ? 'rgba(24,135,66,0.08)' : 'transparent',
                color: activeReport === tab.id ? '#188742' : '#101828',
                fontWeight: activeReport === tab.id ? 600 : undefined,
                borderRadius: 12,
                padding: '10px 18px',
                border: 'none',
                cursor: 'pointer',
                fontSize: 14,
              }}
            >
              {tab.label}
            </button>
          ))}
        </div>
      </div>

      <div className="mt-8 w-full rounded-2xl p-6 border border-[#18181B0F] bg-[#fcfcfc]">
      <div className="flex items-center justify-between mb-6">
  <h2 className="text-lg font-semibold text-gray-900">
    {CARD_TITLES[activeReport] || CARD_TITLES.product}
  </h2>

  <div className="flex items-center gap-[18px]">
    <div className="relative flex items-center">
      <select
        value={selectedCompanyId}
        onChange={(e) => setSelectedCompanyId(e.target.value)}
        className="rounded-[12px] border-[1.5px] border-[#188742] bg-white text-[#188742] font-semibold appearance-none cursor-pointer transition hover:bg-[#188742]/5 disabled:opacity-50 disabled:cursor-not-allowed whitespace-nowrap pr-10"
        style={{ height: 48, paddingLeft: 22, paddingRight: 40, minWidth: 180 }}
      >
        <option value="">All Companies</option>
        {companies.map((company) => (
          <option key={company._id || company.id} value={company._id || company.id}>
            {company.companyCode ? `${company.companyCode} - ${company.name}` : company.name}
          </option>
        ))}
      </select>
      <span className="pointer-events-none absolute right-3 flex items-center text-[#188742]" style={{ top: '50%', transform: 'translateY(-50%)' }}>
        <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth={2} stroke="currentColor" className="w-5 h-5">
          <path strokeLinecap="round" strokeLinejoin="round" d="M19.5 8.25l-7.5 7.5-7.5-7.5" />
        </svg>
      </span>
    </div>

    <button
      type="button"
      onClick={() => handleDownloadReport(activeReport)}
      disabled={downloading || loading}
      className="flex items-center gap-2 rounded-[12px] border-[1.5px] border-[#188742] bg-white text-[#188742] font-semibold transition hover:bg-[#188742]/5 active:scale-[0.98] disabled:opacity-50 disabled:cursor-not-allowed whitespace-nowrap"
      style={{ height: 48, paddingLeft: 22, paddingRight: 22 }}
    >
      <FiDownload className="w-4 h-4" />
      {downloading ? "Downloading..." : "Export CSV"}
    </button>
  </div>
</div>


        {activeReport === 'product' && (
          <Table
            columns={PRODUCT_COLUMNS}
            data={productData}
            loading={loading}
            emptyMessage="No data available"
          />
        )}

        {activeReport === 'batch' && (
          <Table
            columns={BATCH_COLUMNS}
            data={batchData}
            loading={loading}
            emptyMessage="No data available"
          />
        )}

        {activeReport === 'uom' && (
          <>
            {loading ? (
              <div className="py-12 text-center text-sm text-gray-500">Loading...</div>
            ) : (
              <>
                <div className="mt-6 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6 w-full">
  <SummaryCard
    label="Total Products Out of Stock"
    value={uomData.filter((r) => (r.qnt ?? 0) === 0).length}
    Icon={TotalProductsIcon}
    bg="#DC2626"   // red
    stroke={true}
  />

  <SummaryCard
    label="Total Products (in report)"
    value={uomData.length}
    Icon={TotalBinsIcon}
    bg="#01C36F"   // green
    stroke={true}
  />

  <SummaryCard
    label="Total UOMs"
    value={[...new Set(uomData.map((r) => r.uom))].length}
    Icon={TotalProductsIcon}
    bg="#4F46E5"   // blue/purple
    stroke={true}
  />
</div>

                <div className="mt-8">
                  <Table
                    columns={UOM_COLUMNS}
                    data={uomData}
                    loading={loading}
                    emptyMessage="No data available"
                  />
                </div>
              </>
            )}
          </>
        )}

        {activeReport === 'summary' && (
          <>
            {loading ? (
              <div className="py-12 text-center text-sm text-gray-500">Loading...</div>
            ) : summaryData ? (
              <div className="mt-6 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6 w-full">
                <SummaryCard
                  label="Total Products"
                  value={summaryData.totalProducts ?? 0}
                  icon={<RiBox3Line className="w-4 h-4" />}
                  iconBgColor="bg-emerald-50"
                  iconTextColor="text-emerald-600"
                />
                <SummaryCard
                  label="Total Opening Stock"
                  value={summaryData.totalOpeningStock ?? 0}
                  icon={<MdInventory className="w-4 h-4" />}
                  iconBgColor="bg-blue-50"
                  iconTextColor="text-blue-700"
                />
                <SummaryCard
                  label="Total Inward"
                  value={summaryData.totalInwardQuantity ?? 0}
                  icon={<IoTrendingDown className="w-4 h-4" />}
                  iconBgColor="bg-emerald-50"
                  iconTextColor="text-emerald-600"
                />
                <SummaryCard
                  label="Total Outward"
                  value={summaryData.totalOutwardQuantity ?? 0}
                  icon={<IoAnalyticsOutline className="w-4 h-4" />}
                  iconBgColor="bg-blue-50"
                  iconTextColor="text-blue-700"
                />
                <SummaryCard
                  label="Total Return"
                  value={summaryData.totalReturnQuantity ?? 0}
                  icon={<IoIosReturnLeft className="w-4 h-4" />}
                  iconBgColor="bg-blue-50"
                  iconTextColor="text-blue-700"
                />
                <SummaryCard
                  label="Total Closing Stock"
                  value={summaryData.totalClosingStock ?? 0}
                  icon={<MdInventory className="w-4 h-4" />}
                  iconBgColor="bg-emerald-50"
                  iconTextColor="text-emerald-600"
                />
              </div>
            ) : (
              <div className="py-12 text-center text-sm text-gray-500">No data available</div>
            )}
          </>
        )}

        {activeReport === 'aging' && (
          <div className="space-y-8">
            {loading ? (
              <div className="py-12 text-center text-sm text-gray-500">Loading...</div>
            ) : (
              <>
                <div className="mt-6 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6 w-full">
                  <SummaryCard
                    label="Expired Batches"
                    value={ageingSummary?.expiredBatchCount ?? expiredBatches.length}
                    icon={<MdInventory className="w-4 h-4" />}
                    iconBgColor="bg-red-50"
                    iconTextColor="text-red-600"
                  />
                  <SummaryCard
                    label="Near Expiry (30 Days)"
                    value={ageingSummary?.nearExpiryBatchCount ?? nearExpiryBatches.length}
                    icon={<MdInventory className="w-4 h-4" />}
                    iconBgColor="bg-amber-50"
                    iconTextColor="text-amber-600"
                  />
                  <SummaryCard
                    label="Good Batches"
                    value={ageingSummary?.goodBatchCount ?? 0}
                    icon={<MdInventory className="w-4 h-4" />}
                    iconBgColor="bg-emerald-50"
                    iconTextColor="text-emerald-600"
                  />
                </div>
                <div>
                  <h3 className="text-base font-semibold text-gray-900 mb-4">Expired Batches</h3>
                  <Table
                    columns={EXPIRED_COLUMNS}
                    data={expiredBatches}
                    loading={loading}
                    emptyMessage="No expired batches"
                  />
                </div>
                <div>
                  <h3 className="text-base font-semibold text-gray-900 mb-4">Near Expiry Batches (Next 30 Days)</h3>
                  <Table
                    columns={NEAR_EXPIRY_COLUMNS}
                    data={nearExpiryBatches}
                    loading={loading}
                    emptyMessage="No near expiry batches"
                  />
                </div>
              </>
            )}
          </div>
        )}
      </div>
    </div>
  );
}
